import { useCompetencyVisibility } from './useCompetencyVisibility';
import { usePortalRecognitions } from './usePortalReads';

import { isRecognitionModuleInactiveError } from '@/utils/recognitionErrors';

/**
 * Whether the summary tab has anything behind it, or `undefined` while an answer it rests on is
 * still on its way -- or could not be read at all. The three states are distinct on purpose: read as
 * `false`, "not known yet" would withhold the tab from a reader who does have content, then hand it
 * back a beat later. The page tells a failure from a wait, and says so rather than waiting on.
 *
 * The tab's three sections do not stand or fall together. The two competency ones are the company's
 * choice, taken on the Competencies page. The recognitions are not a choice: they are absent from a
 * company whose plan has no Recognition module, and present for every company that has it -- an
 * employee nobody has recognized yet is invited to recognize a colleague, which is content, not
 * emptiness.
 */
export const useIsSummaryTabOffered = (): boolean | undefined => {
    const { areCompetenciesShown } = useCompetencyVisibility();
    const recognitions = usePortalRecognitions();

    if (areCompetenciesShown === undefined) {
        return undefined;
    }

    // With the competencies shown the tab already holds two sections, so what Engagement answers can
    // neither add it nor take it away: nothing waits on that answer here.
    if (areCompetenciesShown) {
        return true;
    }

    return recognitions.isPending
        ? undefined
        : !isRecognitionModuleInactiveError(recognitions.error);
};
